<template>
  <div
    class="base-card"
    role="article"
    :aria-describedby="`content-${id}`"
    @click.stop.prevent="handleCardClick"
  >
    <div class="row-1">
      <div class="tags">
        <slot name="tags" />
        <a
          v-if="result.tagLink"
          :href="result.tagLink"
          class="tag"
          :class="result.tagLink ? 'tag-link' : ''"
          @click.stop
        >
          {{ result.tagName }}
        </a>
        <a
          v-if="result.orgLink"
          :href="result.orgLink || '/'"
          class="tag"
          :class="result.tagLink ? 'tag-link' : ''"
          @click.stop
        >
          {{ result.orgName }}
        </a>
      </div>
      <div class="likes-section">
        <slot name="likes-section" />
        <button
          type="button"
          :disabled="isFeedbackDisabled"
          :aria-label="$t('search.likeButtonAriaLabel')"
          @click.stop="handleLike"
        >
          <Icon
            name="lucide:thumbs-up"
            size="14"
            mode="svg"
            :class="{ 'icon-active': isLiked }"
          />
        </button>
        <button
          type="button"
          :disabled="isFeedbackDisabled"
          :aria-label="$t('search.dislikeButtonAriaLabel')"
          @click.stop="handleDislike"
        >
          <Icon
            name="lucide:thumbs-down"
            size="14"
            mode="svg"
            :class="{ 'icon-active': isDisliked }"
          />
        </button>
      </div>
    </div>
    <div class="row-2">
      <div
        v-if="$slots.preview"
        class="image-preview"
      >
        <slot name="preview" />
      </div>
      <div
        :id="`content-${id}`"
        class="content-section"
      >
        <slot name="result-content" />
      </div>
    </div>

    <div
      v-if="isPopoverActive"
      class="feedback-popover"
    >
      <span class="title">{{ $t('search.thankYou') }}</span>
      <span class="description">{{ $t('search.yourFeedbackHelpsUsImprove') }}</span>
      <a
        class="button share-more-feedback"
        :aria-label="$t('search.shareMoreFeedback')"
        @click.stop="openFeedbackModal"
        >{{ $t('search.shareMoreFeedback') }}</a
      >
      <a
        class="button"
        :aria-label="$t('search.close')"
        @click.stop="closeFeedbackPopover"
        >{{ $t('search.close') }}</a
      >
    </div>
  </div>
</template>

<script setup>
import { useFeedback } from '@/composables/useFeedback';
import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';

const props = defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
  isContentLoading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const id = useId();
const isLiked = ref(false);
const isDisliked = ref(false);

const localFeedbackData = ref(null);
const localFeedbackSentiment = ref(null);

const isPopoverActive = computed(() => store.getters.getActiveFeedbackPopoverId === id);
const isFeedbackDisabled = computed(() => props.isContentLoading || feedbackLoading.value);

const emits = defineEmits(['openFeedbackModal', 'result-clicked']);
const { isLoading: feedbackLoading, submitFeedback } = useFeedback();

const popoverTimer = ref(null);

watch(isPopoverActive, (newValue, oldValue) => {
  if (newValue === true) {
    popoverTimer.value = setTimeout(() => {
      store.commit('clearActiveFeedbackPopover');
    }, 5000);
  } else if (oldValue === true && newValue === false) {
    clearTimeout(popoverTimer.value);
    if (localFeedbackData.value && localFeedbackSentiment.value) {
      submitFeedback(localFeedbackData.value, localFeedbackSentiment.value);
    }
    localFeedbackData.value = null;
    localFeedbackSentiment.value = null;
  }
});

const handleLike = async () => {
  isLiked.value = !isLiked.value;

  if (isLiked.value) {
    isDisliked.value = false;
    if (props.result?.id && props.result?.type) {
      localFeedbackData.value = props.result;
      localFeedbackSentiment.value = 'positive';
      store.commit('setFeedbackData', props.result);
      store.commit('setFeedbackSentiment', 'positive');
    }
    store.commit('setActiveFeedbackPopoverId', id);
  }
};

const handleDislike = async () => {
  isDisliked.value = !isDisliked.value;

  if (isDisliked.value) {
    isLiked.value = false;
    if (props.result?.id && props.result?.type) {
      localFeedbackData.value = props.result;
      localFeedbackSentiment.value = 'negative';
      store.commit('setFeedbackData', props.result);
      store.commit('setFeedbackSentiment', 'negative');
    }
    store.commit('setActiveFeedbackPopoverId', id);
  }
};

const openFeedbackModal = () => {
  emits('openFeedbackModal');
  store.commit('clearActiveFeedbackPopover');
};

const closeFeedbackPopover = () => {
  store.commit('clearActiveFeedbackPopover');
};
const handleCardClick = () => {
  emits('result-clicked');
};
</script>

<style lang="scss" scoped>
@use '../../global/scss/mixins/_breakpoints.scss' as bp;
@use '../../global/scss/mixins/_text.scss';

.base-card {
  --color-grey-75: #e9e9e9;
  --color-grey-100: #e0e0e0;
  --color-gray-200: #d3d3d3;
  --color-grey-500: #949494;
  --color-grey-950: #2d2d2d;
  --color-blue-500: #1850c1;
  --color-blue-50: #f3f6fc;
  --color-text-primary: #2d2d2d;
  --color-text-secondary: #474747;
  --color-text-tertiary: var(--color-grey-500);
  --color-purple-500: #662e80;
  --border-color: var(--color-grey-100);

  border-radius: 4px;
  border: 1px solid var(--border-color);
  padding: 16px;
  text-decoration: none;
  gap: 32px;

  position: relative;

  &:hover {
    box-shadow:
      0px 4px 6px -1px rgba(0, 0, 0, 0.1),
      0px 0px 5px 0px rgba(0, 0, 0, 0.12);
    cursor: pointer;
  }

  &:has(:focus-visible) {
    border: var(--color-purple-500) solid 1px;
    outline: var(--color-blue-500) solid 1px;
    outline-offset: 2px;
    border-radius: 8px;
  }

  a,
  button {
    &:focus-visible {
      outline: var(--color-blue-500) solid 1px;
      outline-offset: 2px;
    }
  }

  .row-1 {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .row-2 {
    display: flex;
    gap: 16px;
  }

  .image-preview {
    display: flex;
    align-items: flex-start;
  }

  .content-section {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }

  .likes-section {
    color: var(--color-grey-500);
    display: flex;
    gap: 12px;

    button {
      border-radius: 4px;
      background: none;
      border: none;
      color: inherit;
      padding: 0px;
      transition: all 0.2s ease;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        color: var(--color-grey-500);
        transform: scale(1.1);
      }
    }

    .icon-active {
      color: var(--primary-button-background);
      animation: iconActivate 0.4s ease-out;
      transform: scale(1.1);
    }
  }

  .feedback-popover {
    position: absolute;
    top: 55px;
    right: 10px;
    width: 264px;
    height: 120px;
    padding: 10px;
    background-color: var(--primary-white);
    border: 1px solid var(--color-gray-200);
    z-index: 10;
    border-radius: 8px;
    box-sizing: border-box;
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.12);
    box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
    .title {
      display: block;
      letter-spacing: 0px;
      color: var(--color-text-primary);
      margin-bottom: 8px;
    }
    .description {
      color: var(--color-text-secondary);
      line-height: 22px;
      margin-bottom: 8px;
      display: block;
    }
    .button {
      color: var(--primary-button-background);
      text-decoration: none;
    }
    .share-more-feedback {
      margin-right: 24px;
    }
  }

  @keyframes iconActivate {
    0% {
      transform: scale(1);
      color: var(--color-grey-950);
    }
    50% {
      transform: scale(1.4);
      color: var(--primary-button-background);
    }
    100% {
      transform: scale(1.1);
      color: var(--primary-button-background);
    }
  }

  :deep(.preview-image),
  :deep(.preview-fallback) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 113px;
    aspect-ratio: 4/3;
    border-radius: 4px;
    background-color: var(--background-color);
    color: var(--text-color);

    img {
      width: 100%;
      aspect-ratio: inherit;
      object-fit: cover;
      border-radius: inherit;
    }

    @include bp.screen('<=sm') {
      width: 96px;
      height: 72px;
      aspect-ratio: 4/3;
    }
  }

  :deep(.title) {
    color: var(--color-text-primary);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-decoration: none;
    overflow-wrap: anywhere;
    line-height: 1.2;
    @include text.line-clamp(2);
  }

  :deep(.snippet) {
    color: var(--color-text-secondary);
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.5px;
    word-break: break-word;
    line-height: 1.3;
    @include text.line-clamp(2);
  }

  :deep(.meta-info) {
    display: flex;
    gap: 24px;
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.1px;
    .date,
    .location {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
      color: var(--color-text-secondary);
      font-size: 12px;
      font-weight: 400;
      line-height: 19px;
      letter-spacing: 0.5px;
    }
    span {
      color: var(--color-text-primary);
    }
  }

  .tags {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 450;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-decoration: none;
    .tag {
      display: inline-flex;
      padding: 4px 8px;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      background: var(--color-grey-75);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-decoration: none;
      cursor: default;
      color: var(--color-text-secondary);
      @include bp.screen('<=sm') {
        padding: 2px 7px;
      }
      &.tag-link:hover {
        cursor: pointer;
        background: var(--color-grey-100);
      }
    }
  }
}

@include bp.screen('<=sm') {
  .base-card {
    gap: 16px;
  }

  .tag {
    padding: 2px 7px;
  }
}
</style>
